import { useCallback, useEffect, useRef, useState } from 'react';
import type {
    SmriqLastCompletedServiceOption,
    SmriqLastCompletedServiceSelection,
    SmriqSchedulePreviewResult,
} from '../types/smriq.types';
import { isAutoMatch } from '../utils/isAutoMatch';
import { defaultLastCompletedServiceSelection } from '../utils/defaultLastCompletedServiceSelection';
import { isSelectionOffered } from '../utils/isSelectionOffered';
import type { SmriqJourneyError } from './useSmriqJourney';

export type LastCompletedServiceOptionsState = 'loading' | 'failed' | 'unavailable' | 'empty' | 'ready';

interface UseSmriqLastCompletedServiceArgs {
    preview: SmriqSchedulePreviewResult | null;
    isPreviewLoading: boolean;
    previewError: SmriqJourneyError | null;
    savedSelection: SmriqLastCompletedServiceSelection | null | undefined;
    savedSettings?: unknown;
    previewSettled: boolean;
    sessionId: string | null;
    canResumePreview: boolean;
    retryPreview: () => void;
    supported: boolean;
    markStale: () => void;
    vehicleLastServiceDate?: string;
    vehicleLastServiceMileage?: number;
}

interface VehicleLastService {
    date?: string;
    mileage?: number;
}

interface SelectionState {
    saved: SmriqLastCompletedServiceSelection | null | undefined;
    settings: unknown;
    selection: SmriqLastCompletedServiceSelection | null;
    valid: boolean;
    resetKey: number;
    withdrawn: boolean;
}

const EMPTY_OPTIONS: SmriqLastCompletedServiceOption[] = [];
const EMPTY_WARNINGS: string[] = [];

const initialState = (
    saved: SmriqLastCompletedServiceSelection | null | undefined,
    settings: unknown,
    resetKey: number,
    vehicle: VehicleLastService,
): SelectionState => ({
    saved,
    settings,
    selection: defaultLastCompletedServiceSelection({
        saved,
        hasSavedSettings: settings != null || saved != null,
        vehicleLastServiceDate: vehicle.date,
        vehicleLastServiceMileage: vehicle.mileage,
    }),
    valid: true,
    resetKey,
    withdrawn: false,
});

function optionsState(
    preview: SmriqSchedulePreviewResult | null,
    isPreviewLoading: boolean,
    previewError: SmriqJourneyError | null,
    optionCount: number,
): LastCompletedServiceOptionsState {
    if (isPreviewLoading) return 'loading';
    if (previewError) return 'failed';
    if (!preview) return 'unavailable';
    return optionCount === 0 ? 'empty' : 'ready';
}

/** Owns the dealer's last-completed-service choice and what the schedule preview says about it. */
export function useSmriqLastCompletedService({
    preview,
    isPreviewLoading,
    previewError,
    savedSelection,
    savedSettings,
    previewSettled,
    sessionId,
    canResumePreview,
    retryPreview,
    supported,
    markStale,
    vehicleLastServiceDate,
    vehicleLastServiceMileage,
}: UseSmriqLastCompletedServiceArgs) {
    const vehicleRef = useRef<VehicleLastService>({});
    vehicleRef.current = { date: vehicleLastServiceDate, mileage: vehicleLastServiceMileage };
    const [state, setState] = useState<SelectionState>(() =>
        initialState(savedSelection, savedSettings, 0, vehicleRef.current),
    );

    let current = state;
    if (state.saved !== savedSelection || state.settings !== savedSettings) {
        current = initialState(savedSelection, savedSettings, state.resetKey + 1, vehicleRef.current);
        setState(current);
    }

    // A failed preview leaves an earlier result retained; it neither offers options nor blocks.
    const block = previewError ? undefined : preview?.schedule_preview;
    const options = block?.last_completed_service_options ?? EMPTY_OPTIONS;
    const nextService = block?.next_service ?? null;
    const previewWarnings = block?.warnings ?? EMPTY_WARNINGS;
    const canGeneratePlan = block?.availability?.can_generate_plan !== false;

    const selection = supported ? current.selection : null;
    const selectionValid = supported ? current.valid : true;
    const generateAllowed = canGeneratePlan && selectionValid;
    // The vendor resolves an Auto match, so it never waits on the preview's option list.
    const selectionPendingValidation =
        selection !== null &&
        !isAutoMatch(selection) &&
        !(preview && !previewError && isSelectionOffered(selection, options));
    const selectionWithdrawn = supported && current.withdrawn;

    useEffect(() => {
        const settled = !!preview && !previewError && !isPreviewLoading && previewSettled;
        if (!supported || !settled || !selection || isSelectionOffered(selection, options)) return;
        setState((previous) => ({
            ...previous,
            selection: null,
            valid: true,
            withdrawn: true,
            resetKey: previous.resetKey + 1,
        }));
        markStale();
    }, [preview, previewError, isPreviewLoading, previewSettled, supported, selection, options, markStale]);

    const resumedSessionRef = useRef<string | null>(null);
    useEffect(() => {
        if (preview || previewError || isPreviewLoading || !previewSettled) return;
        if (!canResumePreview || !sessionId || resumedSessionRef.current === sessionId) return;
        resumedSessionRef.current = sessionId;
        retryPreview();
    }, [preview, previewError, isPreviewLoading, previewSettled, canResumePreview, sessionId, retryPreview]);

    const changeSelection = useCallback(
        (next: SmriqLastCompletedServiceSelection | null, valid: boolean) => {
            setState((previous) => ({ ...previous, selection: next, valid, withdrawn: false }));
            markStale();
        },
        [markStale],
    );

    const revertSelection = useCallback(() => {
        setState((previous) =>
            initialState(previous.saved, previous.settings, previous.resetKey + 1, vehicleRef.current),
        );
    }, []);

    return {
        supported,
        options,
        optionsState: optionsState(preview, isPreviewLoading, previewError, options.length),
        nextService,
        canGeneratePlan,
        previewWarnings,
        retryPreview,
        selection,
        selectionValid,
        selectionResetKey: current.resetKey,
        changeSelection,
        revertSelection,
        generateAllowed,
        selectionWithdrawn,
        selectionPendingValidation,
        autoGenerateReady:
            previewSettled &&
            generateAllowed &&
            !selectionPendingValidation &&
            !selectionWithdrawn,
    };
}
